/**
 * What a key means to an open dialog, where it opens focused, and when it may be submitted.
 *
 * Pure and file-local for the reason `libraryRows.ts` and `logTabs.ts` are: `npm test` has no DOM,
 * so a rule is only reachable if it is not inside a component. Duck-typed rather than
 * instance-checked, in the manner of `lib/dom.ts` — a guard deciding whether Enter deletes
 * fourteen notes should not go untested because a `KeyboardEvent` was awkward to construct.
 *
 * ## Why the dialog reads keys at all, when a `<form>` would normally
 *
 * `Dialogs.tsx` shields the app with a capture-phase `stopImmediatePropagation` on `window`, the
 * way `Lightbox.tsx` does. React 19 attaches its own listeners to the **root container**, which is
 * below `window` in the capture phase — so while a dialog is open, React's synthetic `onKeyDown`
 * never fires anywhere, *including inside the dialog's own input*. There is therefore nowhere else
 * for Enter and Escape to be decided, and no `<form>` submit to lean on.
 *
 * Typing still works: characters are the default action rather than a listener, and `onChange`
 * rides `input`, a different event type. So is the IME — which is why `isComposing` is in the
 * table below rather than an afterthought.
 */

export type DialogAction = "submit" | "cancel" | "focus-next" | "focus-prev" | "swallow";

export type DialogKind = "confirm" | "prompt";

/**
 * What this keystroke means.
 *
 * `swallow` is not "do nothing" — it means *the app behind the scrim must not see this*, which is
 * the whole point of the shield. The browser's own default action still runs, so a letter still
 * reaches the field and Space still activates a focused button.
 */
export function dialogKey(
  event: { key: string; shiftKey?: boolean; isComposing?: boolean },
  state: { kind: DialogKind; canSubmit: boolean },
): DialogAction {
  // Escape while an IME is converting means *dismiss the candidate window*, not *throw away the
  // name I was typing*. Measured rather than assumed, because it looks like it could go either
  // way: Chromium 1194 reports `key: "Escape", isComposing: true, keyCode: 27` for a keydown
  // during a live composition. So without this, one Escape aimed at the candidate list also
  // cancels the dialog and loses a half-typed サクラノ詩. The IME ends the composition on that
  // press, so the next Escape arrives with `isComposing: false` and closes the dialog as normal —
  // two presses, which is what a Japanese typist already expects everywhere else.
  if (event.key === "Escape") return event.isComposing ? "swallow" : "cancel";

  if (event.key === "Tab") return event.shiftKey ? "focus-prev" : "focus-next";

  if (event.key === "Enter") {
    // A Japanese IME commits a conversion with Enter, and that keystroke arrives here like any
    // other. Without this, accepting the kanji in サクラノ詩 would submit the half-typed name —
    // the same trap `AskPanel`'s composer documents, and it is not hypothetical in this app.
    if (event.isComposing) return "swallow";
    // A confirm's focus is on a button, so the browser's own activation fires the right one —
    // and `defaultFocus` is what makes "the right one" safe. Submitting here as well would
    // double-fire, and would ignore which button the reader had tabbed to.
    if (state.kind === "confirm") return "swallow";
    return state.canSubmit ? "submit" : "swallow";
  }

  return "swallow";
}

/**
 * Where focus lands when the dialog opens.
 *
 * **A danger confirm opens on Cancel.** `Cards.tsx`'s delete is a hard delete with no trash, and
 * the confirm is the only thing in front of it (PRD decision 3) — so Enter, pressed by someone
 * who has not read the box, must not be the button that empties it. A default-tone confirm is the
 * opposite case: nothing is lost, so Enter should agree with it.
 */
export function defaultFocus(request: {
  kind: DialogKind;
  tone?: "default" | "danger";
}): "input" | "confirm" | "cancel" {
  if (request.kind === "prompt") return "input";
  return request.tone === "danger" ? "cancel" : "confirm";
}

/**
 * The trimmed value, and whether it may be submitted at all.
 *
 * This exists because **the seven prompts it replaces disagreed with each other**, and two of the
 * disagreements were invisible: `Cards.tsx`'s due-date read `if (spec)`, so clearing the field
 * cancelled, while its reposition read `!== null`, so clearing the field meant position 0. Both
 * behaviours are wanted; what was missing was anywhere to say so. Each call site now passes
 * `allowEmpty` and the difference is declared rather than emergent.
 *
 * Trimming here also closes a real bug: `DecksPanel` and `TagsPanel` validated `name.trim()` and
 * then sent the **untrimmed** `name`, so a deck typed with a trailing space was created with one.
 */
export function promptValue(
  request: { allowEmpty?: boolean },
  raw: string,
): { value: string; canSubmit: boolean } {
  const value = raw.trim();
  return { value, canSubmit: request.allowEmpty === true || value.length > 0 };
}

/**
 * May a request open right now?
 *
 * One dialog at a time, and this is bookkeeping rather than taste. `useHistoryNav`'s `openOverlay`
 * early-returns on an id already open, so a second dialog would push **no** history entry — and
 * then the first close would consume the one entry that did exist, leaving the second dialog on
 * screen with Back already spent on it.
 *
 * Unreachable by gesture, because the scrim covers the app. It guards against code, which is
 * exactly the sort of thing that is worth a line in the suite rather than a line in a comment.
 */
export function acceptRequest(current: unknown | null): boolean {
  return current === null;
}
